import cronstrue from "cronstrue";
import React from "react";
import { Input, InputGroup } from "reactstrap";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";
import { timezoneOptions } from "common/Timezone";

export const invoiceIntervalPresets: ReadonlyArray<{ labelKey: OfficeTranslationKey; value: string }> = [
    { labelKey: "accounts.invoiceIntervalField.disabled", value: "" },
    { labelKey: "accounts.invoiceIntervalField.everyDay", value: "0 9 * * *" },
    { labelKey: "accounts.invoiceIntervalField.everyMonday", value: "0 9 * * 1" },
    { labelKey: "accounts.invoiceIntervalField.everyMonth", value: "0 9 1 * *" },
    { labelKey: "accounts.invoiceIntervalField.everyQuarter", value: "0 9 1 1,4,7,10 *" },
    { labelKey: "accounts.invoiceIntervalField.everyYear", value: "0 9 1 1 *" },
];

const timezones = timezoneOptions();

export const describeInvoiceInterval = (value: string, locale: "en" | "nl"): string | null => {
    if (!value.trim()) return null;
    try {
        return cronstrue.toString(value, { use24HourTimeFormat: true, locale });
    } catch {
        return null;
    }
};

const InvoiceIntervalField: React.FC<{
    value: string;
    onChange: (value: string) => void;
    timezone?: string;
    onTimezoneChange?: (value: string) => void;
}> = ({ value, onChange, timezone, onTimezoneChange }) => {
    const { locale, t } = useOfficeTranslation();
    const preset = invoiceIntervalPresets.some(option => option.value === value) ? value : "custom";
    const description = describeInvoiceInterval(value, locale);
    const invalid = value.trim() !== "" && description === null;
    return <div className="mb-3">
        <label className="form-label" htmlFor="invoice-interval-preset">{t("accounts.invoiceIntervalField.invoiceInterval")}</label>
        <InputGroup>
            <Input
                id="invoice-interval-preset"
                type="select"
                value={preset}
                aria-label={t("accounts.invoiceIntervalField.invoiceIntervalPreset")}
                style={{ flex: "1 1 60%" }}
                onChange={event => {
                    if (event.target.value !== "custom") onChange(event.target.value);
                }}
            >
                {invoiceIntervalPresets.map(option =>
                    <option key={option.value || "disabled"} value={option.value}>{t(option.labelKey)}</option>
                )}
                <option value="custom">{t("accounts.invoiceIntervalField.customSchedule")}</option>
            </Input>
            <Input
                id="invoice-interval-cron"
                className="font-monospace"
                value={value}
                aria-label={t("accounts.invoiceIntervalField.cronExpression")}
                style={{ flex: "1 1 40%" }}
                onChange={event => onChange(event.target.value)}
                placeholder={t("accounts.invoiceIntervalField.cronExpression.2218a7d")}
            />
        </InputGroup>
        {timezone !== undefined && onTimezoneChange && <div className="mt-2">
            <label className="form-label" htmlFor="invoice-closing-timezone">
                {t("accounts.invoiceIntervalField.timezone")}
            </label>
            <Input
                id="invoice-closing-timezone"
                list="invoice-closing-timezones"
                value={timezone}
                onChange={event => onTimezoneChange(event.target.value)}
            />
            <datalist id="invoice-closing-timezones">
                {timezones.map(option => <option key={option} value={option} />)}
            </datalist>
        </div>}
        <small className={`d-block mt-2 ${invalid ? "text-danger" : "text-muted"}`}>
            <i className="mdi mdi-calendar-clock-outline me-1" />
            {description ?? t(invalid ? "accounts.invoiceIntervalField.invalid" : "accounts.invoiceIntervalField.disabledDescription")}
        </small>
    </div>;
};

export default InvoiceIntervalField;
